import { Formik, Form, useFormikContext } from 'formik';
import { noop } from 'lodash';
import { useCurrentStateAndParams } from '@uirouter/react';

import { notifySuccess } from '@/portainer/services/notifications';

import { Widget, WidgetBody } from '@@/Widget';
import { Alert } from '@@/Alert';
import { LoadingButton } from '@@/buttons';
import { confirm } from '@@/modals/confirm';
import { ModalType } from '@@/modals/Modal/types';
import { buildConfirmButton } from '@@/modals/utils';

import { useAddon } from '../queries/useAddon';
import { useUpgradeAddon } from '../queries/useUpgradeAddon';
import { ConfigurationStep } from '../InstallView/ConfigurationStep';
import { InstallFormValues } from '../InstallView/types';
import { trimValues } from '../InstallView/trimValues';
import { isUpgradeRequested } from '../upgradeDeepLink';
import { getAddonLifecycleState } from '../addonLifecycleState';

import { useAddonReleaseValues } from './queries/useAddonReleaseValues';
import { useAddonUpgradeState } from './useAddonUpgradeState';
import { buildOverrides } from './addonOverrides';

export function ConfigurationTab() {
  const { params } = useCurrentStateAndParams();
  const addonId = String(params.id ?? '');
  const addonQuery = useAddon(addonId);
  const record = addonQuery.data?.record;
  const chartVersion = record?.chartVersion;
  const releaseValuesQuery = useAddonReleaseValues(addonId);

  const { isInProgress } = getAddonLifecycleState(addonQuery.data);
  const editable = releaseValuesQuery.isSuccess && !isInProgress;

  // Auto-select newer version only when arriving from Upgrade action
  const wantsUpgrade = isUpgradeRequested(params);
  const latest = record?.availableVersions?.[0];
  const initialVersion =
    wantsUpgrade && record?.upgradeAvailable && latest
      ? latest
      : (chartVersion ?? '');

  const initialValues: InstallFormValues = {
    version: initialVersion,
    values: {},
    teams: [],
    denyPortainerAccess: false,
  };

  // Wait for release baseline to settle before enabling submit
  const baselineReady = releaseValuesQuery.isFetched;

  return (
    <div className="row">
      <div className="col-sm-12">
        <Widget aria-label="Configuration">
          <WidgetBody loading={addonQuery.isLoading}>
            {addonQuery.isError && (
              <Alert color="error">
                Unable to load the add-on&apos;s chart versions.
              </Alert>
            )}
            {releaseValuesQuery.isError && (
              <Alert
                color="warn"
                title="Current configuration unavailable"
                className="mb-2"
              >
                The add-on&apos;s configuration could not be loaded - the Helm
                release may have been removed outside Portainer, or the
                installation may have failed. The values shown are the chart
                defaults.
              </Alert>
            )}
            {addonQuery.data && !addonQuery.isError && (
              <Formik<InstallFormValues>
                initialValues={initialValues}
                onSubmit={noop}
              >
                <Form noValidate>
                  <UpgradeFormBody
                    addonId={addonId}
                    editable={editable}
                    installedVersion={chartVersion ?? ''}
                    chartSource={record?.helmChartRepository ?? ''}
                    releaseValues={releaseValuesQuery.data}
                    baselineReady={baselineReady}
                    displayName={addonQuery.data?.displayName ?? 'add-on'}
                  />
                </Form>
              </Formik>
            )}
          </WidgetBody>
        </Widget>
      </div>
    </div>
  );
}

function UpgradeFormBody({
  addonId,
  editable,
  installedVersion,
  chartSource,
  releaseValues,
  baselineReady,
  displayName,
}: {
  addonId: string;
  editable: boolean;
  installedVersion: string;
  chartSource: string;
  releaseValues?: Record<string, unknown>;
  baselineReady: boolean;
  displayName: string;
}) {
  const { values, validateForm, setFieldTouched } =
    useFormikContext<InstallFormValues>();
  const upgradeMutation = useUpgradeAddon(addonId);
  const { fields, isUpgrade, changed, fieldsReady } = useAddonUpgradeState({
    chartSource,
    installedVersion,
    releaseValues,
  });

  // Wait for target version fields to load before allowing submit
  const canSubmit = editable && changed && baselineReady && fieldsReady;

  return (
    <>
      <ConfigurationStep
        readOnly={!editable}
        valuesOverride={releaseValues}
        valuesOverrideReady={baselineReady}
      />

      {editable && (
        <div className="mt-4 flex">
          <LoadingButton
            className="!ml-0"
            type="button"
            color="primary"
            disabled={!canSubmit}
            isLoading={upgradeMutation.isLoading}
            loadingText="Validating"
            onClick={handleClick}
            data-cy="addon-config-upgrade-button"
          >
            {isUpgrade ? 'Upgrade' : 'Apply changes'}
          </LoadingButton>
        </div>
      )}
    </>
  );

  async function handleClick() {
    // Touch errored fields to surface validation errors
    const errors = await validateForm();
    if (errors.values && Object.keys(errors.values).length > 0) {
      fields.forEach((field) =>
        setFieldTouched(`values.${field.key}`, true, false)
      );
      return;
    }

    // Version change restarts the add-on; same-version edit applies in place
    if (isUpgrade) {
      const confirmed = await confirm({
        title: 'Upgrade add-on',
        modalType: ModalType.Warn,
        message: `Upgrade ${displayName} from v${installedVersion} to v${values.version}? This will restart the add-on.`,
        confirmButton: buildConfirmButton('Upgrade'),
      });
      if (!confirmed) {
        return;
      }
    }

    upgradeMutation.mutate(
      {
        version: values.version,
        values: trimValues(buildOverrides(fields, values.values)),
      },
      {
        onSuccess: () => notifySuccess('Add-on upgrade started', displayName),
      }
    );
  }
}
